import { useEffect, useState, useCallback } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import {
  Database,
  Satellite,
  FileLock2,
  Crosshair,
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Clock,
} from 'lucide-react';

const CATEGORY_ICON = {
  database: Database,
  sensors: Satellite,
  audit: FileLock2,
  weapons: Crosshair,
  tracker: Activity,
};

const TRIAL_DATE = new Date('2026-06-01T00:00:00Z');

function daysToTrial() {
  const ms = TRIAL_DATE.getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function PillStat({ label, value, accent = 'sky' }) {
  return (
    <div className="flex flex-col">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className={`text-${accent}-300 font-mono text-sm`}>{value}</div>
    </div>
  );
}

function MongoDetail({ check }) {
  return (
    <div className="space-y-2 text-[11px] font-mono">
      <div className="grid grid-cols-3 gap-3">
        <PillStat label="Mode" value={check.mode || '—'} accent="sky" />
        <PillStat label="Ping" value={check.ping_ok ? 'OK' : 'FAIL'} accent={check.ping_ok ? 'emerald' : 'red'} />
        <PillStat label="Replica Set" value={check.replica_set || '(none)'} accent="zinc" />
      </div>
      {check.note && (
        <div className="text-amber-400/80 text-[10px] leading-snug">⚠ {check.note}</div>
      )}
      {check.error && (
        <div className="text-red-400 text-[10px]">ERROR: {check.error}</div>
      )}
      {check.members && check.members.length > 0 && (
        <div className="space-y-1">
          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Members ({check.members.length})</div>
          {check.members.map((m) => (
            <div key={m.name} className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800 px-2 py-1 rounded-sm">
              <span className="text-zinc-300 truncate">{m.name}</span>
              <span className="flex items-center gap-2">
                <span className={m.state === 'PRIMARY' ? 'text-sky-300' : m.state === 'SECONDARY' ? 'text-emerald-300' : 'text-red-300'}>
                  {m.state}
                </span>
                {m.health === 1 ? <CheckCircle2 size={12} className="text-emerald-400" /> : <XCircle size={12} className="text-red-400" />}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SensorsDetail({ check }) {
  return (
    <div className="space-y-2 text-[11px] font-mono">
      <div className="grid grid-cols-3 gap-3">
        <PillStat label="Online" value={`${check.online} / ${check.total}`} accent={check.ok ? 'emerald' : 'red'} />
        <PillStat label="Coverage" value={check.total ? `${Math.round((check.online / check.total) * 100)}%` : '—'} accent="sky" />
        <PillStat label="Domains" value={Object.keys(check.by_domain || {}).length} accent="zinc" />
      </div>
      <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
        {(check.detail || []).map((s) => {
          const online = s.state === 'online';
          return (
            <div key={s.sensor_id} className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800 px-2 py-1 rounded-sm">
              <span className="text-zinc-300 truncate">{s.sensor_id}</span>
              <span className="flex items-center gap-1.5">
                <span className={`text-[9px] uppercase ${online ? 'text-emerald-300' : 'text-red-300'}`}>{s.state}</span>
                {online ? <CheckCircle2 size={11} className="text-emerald-400" /> : <XCircle size={11} className="text-red-400" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AuditDetail({ check }) {
  return (
    <div className="space-y-2 text-[11px] font-mono">
      <div className="grid grid-cols-3 gap-3">
        <PillStat
          label="Chain"
          value={check.verified ? 'INTACT' : 'TAMPERED'}
          accent={check.verified ? 'emerald' : 'red'}
        />
        <PillStat label="Entries Checked" value={check.entries_checked ?? 0} accent="sky" />
        <PillStat label="Tampered" value={(check.tampered_seq || []).length} accent={(check.tampered_seq || []).length ? 'red' : 'zinc'} />
      </div>
      {check.error && <div className="text-red-400 text-[10px]">ERROR: {check.error}</div>}
      {(check.tampered_seq || []).length > 0 && (
        <div className="text-[10px] text-red-300">
          Tampered seq: {check.tampered_seq.join(', ')}
        </div>
      )}
    </div>
  );
}

function WeaponsDetail({ check }) {
  return (
    <div className="space-y-2 text-[11px] font-mono">
      <div className="grid grid-cols-3 gap-3">
        <PillStat label="Online" value={`${check.online} / ${check.total}`} accent={check.ok ? 'emerald' : 'red'} />
        <PillStat label="Hard kill" value={(check.detail || []).filter((w) => ['anti_drone_gun', 'dew', 'anti_drone_missile', 'kinetic_interceptor', 'net_launcher'].includes(w.weapon_type)).length} accent="red" />
        <PillStat label="Soft kill" value={(check.detail || []).filter((w) => ['rf_jammer', 'gps_spoofer', 'comm_disruptor', 'cyber_takeover', 'acoustic_blaster'].includes(w.weapon_type)).length} accent="emerald" />
      </div>
      <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
        {(check.detail || []).map((w) => {
          const online = w.state === 'online';
          return (
            <div key={w.weapon_id} className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800 px-2 py-1 rounded-sm">
              <span className="text-zinc-300 truncate">{w.weapon_id}</span>
              <span className="flex items-center gap-1.5">
                <span className={`text-[9px] uppercase ${online ? 'text-emerald-300' : 'text-red-300'}`}>{w.state}</span>
                {online ? <CheckCircle2 size={11} className="text-emerald-400" /> : <XCircle size={11} className="text-red-400" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrackerDetail({ check }) {
  const s = check.stats || {};
  return (
    <div className="space-y-2 text-[11px] font-mono">
      <div className="grid grid-cols-4 gap-3">
        <PillStat label="Live tracks" value={s.live_tracks ?? 0} accent="sky" />
        <PillStat label="Contacts in" value={s.contacts_in ?? 0} accent="emerald" />
        <PillStat label="Births" value={s.tracks_born ?? 0} accent="zinc" />
        <PillStat label="Associations" value={s.associations ?? 0} accent="amber" />
      </div>
      {check.error && <div className="text-red-400 text-[10px]">ERROR: {check.error}</div>}
    </div>
  );
}

function CheckCard({ check }) {
  const Icon = CATEGORY_ICON[check.category] || Activity;
  const ok = check.ok;
  return (
    <div
      className={`border rounded-sm p-4 transition-all ${
        ok ? 'border-emerald-500/30 bg-emerald-500/[0.03]' : 'border-red-500/40 bg-red-500/[0.05]'
      }`}
      data-testid={`readiness-check-${check.category}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className={ok ? 'text-emerald-400' : 'text-red-400'} />
          <div>
            <div className="text-sm font-bold uppercase tracking-tight text-zinc-100">{check.name}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{check.category}</div>
          </div>
        </div>
        <div
          className={`flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
            ok
              ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
              : 'border-red-500/50 bg-red-500/10 text-red-300'
          }`}
        >
          {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
          {ok ? 'PASS' : 'FAIL'}
        </div>
      </div>
      {check.category === 'database' && <MongoDetail check={check} />}
      {check.category === 'sensors' && <SensorsDetail check={check} />}
      {check.category === 'audit' && <AuditDetail check={check} />}
      {check.category === 'weapons' && <WeaponsDetail check={check} />}
      {check.category === 'tracker' && <TrackerDetail check={check} />}
    </div>
  );
}

export default function Readiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [auto, setAuto] = useState(true);

  const run = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get('/readiness/check');
      setData(data);
      setLastRun(new Date());
      if (!silent) {
        toast[data.overall_ok ? 'success' : 'error'](
          `Readiness: ${data.go_no_go} — ${data.checks.filter((c) => c.ok).length}/${data.checks.length} systems nominal`,
        );
      }
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'Readiness check failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => run());
  }, [run]);

  useEffect(() => {
    if (!auto) return undefined;
    const id = setInterval(() => run(true), 5000);
    return () => clearInterval(id);
  }, [auto, run]);

  const overall = data?.overall_ok;
  const passCount = data?.checks?.filter((c) => c.ok).length || 0;
  const totalCount = data?.checks?.length || 0;

  return (
    <div className="flex flex-col h-full bg-zinc-950" data-testid="readiness-page">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
              <ShieldCheck size={20} className="text-emerald-400" />
              Pre-Trial Readiness Checklist
            </h1>
            <p className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mt-0.5">
              Live-Fire Trial · Eastern Naval Command · Visakhapatnam
            </p>
          </div>
          <div className="flex items-center gap-3 text-[11px] font-mono uppercase text-zinc-400">
            <span className="flex items-center gap-1.5 text-amber-300">
              <Clock size={12} /> T-{daysToTrial()} days
            </span>
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
                className="accent-sky-500"
                data-testid="auto-refresh-toggle"
              />
              Auto-refresh
            </label>
            <button
              onClick={() => run(false)}
              disabled={loading}
              className="px-3 py-1.5 border border-zinc-700 hover:border-sky-500/60 hover:text-sky-300 rounded-sm flex items-center gap-1.5 disabled:opacity-50"
              data-testid="readiness-run-button"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Re-run
            </button>
          </div>
        </div>

        {/* GO / NO-GO banner */}
        <div
          className={`flex items-center justify-between px-5 py-4 rounded-sm border-2 ${
            overall === undefined
              ? 'border-zinc-700 bg-zinc-900/60'
              : overall
              ? 'border-emerald-500/60 bg-emerald-500/10'
              : 'border-red-500/60 bg-red-500/10'
          }`}
          data-testid="go-no-go-banner"
        >
          <div className="flex items-center gap-4">
            {overall === undefined && <AlertTriangle size={36} className="text-zinc-500" />}
            {overall === true && <ShieldCheck size={36} className="text-emerald-400" />}
            {overall === false && <ShieldOff size={36} className="text-red-400" />}
            <div>
              <div
                className={`text-4xl font-black tracking-tighter ${
                  overall === undefined ? 'text-zinc-400' : overall ? 'text-emerald-300' : 'text-red-300'
                }`}
                data-testid="go-no-go-status"
              >
                {data?.go_no_go || '—'}
              </div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">
                {passCount}/{totalCount} systems nominal
                {lastRun && ` · last check ${lastRun.toLocaleTimeString()}`}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Trial Window</div>
            <div className="text-sky-300 font-mono text-sm">2026-06-01 · Vizag</div>
          </div>
        </div>
      </header>

      {/* Checks grid */}
      <div className="flex-1 overflow-y-auto p-6">
        {!data && (
          <div className="flex items-center justify-center h-full text-zinc-500 font-mono text-sm">
            Running readiness probe…
          </div>
        )}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.checks.map((c) => (
              <CheckCard key={c.category} check={c} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
